import React, { useState, useEffect } from 'react';
import { Smartphone, Download, X, Info } from 'lucide-react';
import PWAInstructionsModal from './PWAInstructionsModal';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const PWAInfoBanner: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  // Check if banner dismissal has expired
  const isDismissalExpired = () => {
    const dismissalData = localStorage.getItem('pwa-banner-dismissed');
    if (!dismissalData) return true; // No dismissal data, show banner
    
    try {
      const parsed = JSON.parse(dismissalData);
      const now = Date.now();
      return now > parsed.expiresAt; // Show banner if expired
    } catch (error) {
      console.error('Error parsing dismissal data:', error);
      return true; // Show banner if parsing fails
    }
  };

  useEffect(() => {
    // Check if app is already installed or running in standalone mode
    const checkInstallStatus = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator as any).standalone;
      
      setIsStandalone(standalone || (isIOS && isInStandaloneMode));
      setIsInstalled(standalone || (isIOS && isInStandaloneMode));
    };

    checkInstallStatus();

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowBanner(false);
    });

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show banner after a delay if dismissal has expired
    const timer = setTimeout(() => {
      if (isDismissalExpired()) {
        console.log('PWA Banner: Showing banner after delay (dismissal expired)');
        setShowBanner(true);
      } else {
        const dismissalData = localStorage.getItem('pwa-banner-dismissed');
        if (dismissalData) {
          const parsed = JSON.parse(dismissalData);
          const timeUntilExpiry = parsed.expiresAt - Date.now();
          const minutesUntilExpiry = Math.ceil(timeUntilExpiry / (1000 * 60));
          console.log(`PWA Banner: Not showing - dismissal expires in ${minutesUntilExpiry} minutes`);
        }
      }
    }, 3000); // Show after 3 seconds

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timer);
    };
  }, [isInstalled, isStandalone]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setShowBanner(false);
      }
      
      setDeferredPrompt(null);
    } else {
      // Show instructions modal for manual installation
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    // Store dismissal with timestamp (expires after 1 minute for testing)
    const dismissalData = {
      timestamp: Date.now(),
      expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours from now
    };
    localStorage.setItem('pwa-banner-dismissed', JSON.stringify(dismissalData));
  };

  // Don't show if not showing
  if (!showBanner) {
    console.log('PWA Banner: Not rendering - showBanner:', showBanner);
    return null;
  }

  console.log('PWA Banner: Rendering banner');

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[9999] bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg shadow-lg p-4 max-w-sm mx-auto">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Smartphone size={20} />
            <h3 className="text-lg font-semibold">
              Install KKoS Tours
            </h3>
          </div>
          <p className="text-sm text-blue-100 mb-3">
            Get the full app experience with offline access, faster loading, and home screen access.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleInstallClick}
              className="flex items-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              <Download size={16} />
              {deferredPrompt ? 'Install App' : 'Install Guide'}
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 text-blue-100 hover:text-white text-sm font-medium transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="ml-2 text-blue-200 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>
      </div>
      
      <PWAInstructionsModal 
        isOpen={showInstructions} 
        onClose={() => setShowInstructions(false)} 
      />
    </div>
  );
};

export default PWAInfoBanner;
